import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaPrint, FaEnvelope, FaHome } from 'react-icons/fa';
import { toast } from 'react-toastify';

const OrderConfirmation = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const order = location.state?.order;

    useEffect(() => {
        if (!order) {
            navigate('/');
            toast.error('No order found');
        }
    }, [order, navigate]);

    if (!order) return null;

    const handlePrint = () => {
        window.print();
    };

    const handleEmail = () => {
        toast.success('Order confirmation sent to your email!');
    };

    return (
        <div className="min-h-screen bg-gray-50 py-12">
            <div className="container mx-auto px-4">
                <div className="max-w-3xl mx-auto">
                    {/* Success Message */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                            <FaCheckCircle className="text-5xl text-green-600" />
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
                            Thank You for Your Order!
                        </h1>
                        <p className="text-gray-600">
                            Your order has been placed successfully. Order #{order.id}
                        </p>
                    </div>

                    {/* Order Details */}
                    <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                        <div className="p-6 border-b bg-gray-50">
                            <h2 className="text-xl font-bold text-gray-800">Order Details</h2>
                        </div>
                        
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <h3 className="font-semibold text-gray-800 mb-2">Shipping Information</h3>
                                    <p className="text-gray-600">
                                        {order.shippingInfo.firstName} {order.shippingInfo.lastName}<br />
                                        {order.shippingInfo.address}<br />
                                        {order.shippingInfo.city}, {order.shippingInfo.state} {order.shippingInfo.zipCode}<br />
                                        {order.shippingInfo.country}<br />
                                        Email: {order.shippingInfo.email}<br />
                                        Phone: {order.shippingInfo.phone}
                                    </p>
                                </div>
                                
                                <div>
                                    <h3 className="font-semibold text-gray-800 mb-2">Order Summary</h3>
                                    <div className="space-y-1 text-sm">
                                        <div className="flex justify-between">
                                            <span>Order Date:</span>
                                            <span>{new Date(order.date).toLocaleDateString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Payment Method:</span>
                                            <span className="capitalize">{order.paymentMethod}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Order Status:</span>
                                            <span className="text-yellow-600 font-semibold capitalize">{order.status}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <h3 className="font-semibold text-gray-800 mb-3">Items Ordered</h3>
                            <div className="space-y-3 mb-6">
                                {order.items.map((item, index) => (
                                    <div key={index} className="flex justify-between items-center border-b pb-3">
                                        <div>
                                            <p className="font-semibold text-gray-800">{item.name}</p>
                                            <p className="text-sm text-gray-500">Quantity: {item.quantity}</p>
                                        </div>
                                        <p className="font-semibold text-gray-800">£{(item.price * item.quantity).toFixed(2)}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="border-t pt-4">
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span>Subtotal:</span>
                                        <span>£{order.subtotal.toFixed(2)}</span>
                                    </div>
                                    {/* <div className="flex justify-between">
                                        <span>Shipping:</span>
                                        <span>{order.shipping === 0 ? 'Free' : `£{order.shipping.toFixed(2)}`}</span>
                                    </div> */}
                                    <div className="flex justify-between">
                                        <span>Tax:</span>
                                        <span>£{order.tax.toFixed(2)}</span>
                                    </div>
                                    {order.discount > 0 && (
                                        <div className="flex justify-between text-green-600">
                                            <span>Discount:</span>
                                            <span>-£{order.discount.toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="border-t pt-2 mt-2">
                                        <div className="flex justify-between text-lg font-bold">
                                            <span>Total:</span>
                                            <span className="text-purple-600">£{order.total.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={handlePrint}
                            className="px-6 py-3 border-2 border-purple-600 text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition flex items-center justify-center gap-2"
                        >
                            <FaPrint /> Print Receipt
                        </button>
                        <button
                            onClick={handleEmail}
                            className="px-6 py-3 border-2 border-purple-600 text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition flex items-center justify-center gap-2"
                        >
                            <FaEnvelope /> Email Confirmation
                        </button>
                        <Link
                            to="/"
                            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:shadow-lg transition flex items-center justify-center gap-2"
                        >
                            <FaHome /> Continue Shopping
                        </Link>
                    </div>

                    {/* Estimated Delivery */}
                    <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                        <p className="text-blue-800">
                            📦 Estimated Delivery: {new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-blue-600 mt-1">
                            You will receive a shipping confirmation email with tracking information
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderConfirmation;